import { useState, useEffect, useRef } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
  useStartAgentMutation,
  useGetAgentsStatusQuery,
  useGetSpecificAgentStatusMutation,
  useUpdateAgentStatusMutation,
} from '../../../data/api/endPointsSlice';

export const useAgentStartStopHandle = () => {
  const [startAgentMutation] = useStartAgentMutation();
  const [updateAgentStatusMutation] = useUpdateAgentStatusMutation();
  const [getSpecificAgentStatus] = useGetSpecificAgentStatusMutation();
  const [statusCheckTimeouts, setStatusCheckTimeouts] = useState<Map<string, NodeJS.Timeout>>(new Map());
  const statusCheckRetriesRef = useRef<Map<string, number>>(new Map());
  const [agentStartedAlready, setAgentStartedAlready] = useState<Map<string, boolean>>(new Map());
  const skipAllAgentsStatusCheck = useRef(false);
  const {
    data: agentStatusList = { statuses: [], total_count: 0 },
    isLoading: agentStatusListLoading,
    error: agentStatusListError,
  } = useGetAgentsStatusQuery(undefined, {
    skip: skipAllAgentsStatusCheck.current,
  });

  // Cleanup useEffect
  useEffect(() => {
    return () => {
      statusCheckTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, [statusCheckTimeouts]);

  const getAgentsStatus = (agentId: string) => {
    const isApiLoaded = !agentStatusListLoading && !agentStatusListError && agentStatusList?.statuses?.length > 0;

    if (!isApiLoaded) return null;

    const statusItem = agentStatusList.statuses.find((item) => item._id === agentId);
    return statusItem?.status;
  };

  const scheduleStatusCheck = (party_id: string, delayMs: number) => {
    const timeoutId = setTimeout(() => {
      handleStoppedAgentStatus(party_id);
    }, delayMs);
    setStatusCheckTimeouts((prev) => new Map(prev).set(party_id, timeoutId));
  };

  const clearStatusCheckForAgent = (party_id: string) => {
    const timeoutId = statusCheckTimeouts.get(party_id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setStatusCheckTimeouts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(party_id);
        return newMap;
      });
    }
    statusCheckRetriesRef.current.delete(party_id);
  };

  const handleStoppedAgentStatus = async (party_id: string) => {
    const RETRY_DELAY = 5000;
    const MAX_RETRIES = 18;
    try {
      const response = await getSpecificAgentStatus({ party_id }).unwrap();

      if (response.status === 'RUNNING') {
        closeSnackbar();
        enqueueSnackbar('Agent is running now', { variant: 'success' });
        clearStatusCheckForAgent(party_id);
        skipAllAgentsStatusCheck.current = false;
        return;
      }

      const currentRetryCount = statusCheckRetriesRef.current.get(party_id) || 0;
      if (currentRetryCount < MAX_RETRIES) {
        const nextRetryCount = currentRetryCount + 1;
        statusCheckRetriesRef.current.set(party_id, nextRetryCount);
        if (currentRetryCount === 4) {
          closeSnackbar();
          enqueueSnackbar('Agent might take time to get started...', {
            variant: 'warning',
            persist: true,
            preventDuplicate: true,
          });
        }
        scheduleStatusCheck(party_id, RETRY_DELAY);
      } else {
        closeSnackbar();
        enqueueSnackbar('Agent start has failed. Please try again.', { variant: 'error' });
        clearStatusCheckForAgent(party_id);
      }
    } catch (error) {
      console.error(`Failed to check agent status for ${party_id}:`, error);
      closeSnackbar();
      enqueueSnackbar('Failed to check agent status', { variant: 'error' });
      clearStatusCheckForAgent(party_id);
    }
  };

  const startAgentWorkflow = async (party_id: string) => {
    const INITIAL_CHECK_DELAY = 5000;
    try {
      await startAgentMutation({ party_id }).unwrap();
      closeSnackbar();
      enqueueSnackbar('Agent is starting...', {
        variant: 'info',
        persist: true,
        preventDuplicate: true,
      });
      scheduleStatusCheck(party_id, INITIAL_CHECK_DELAY);
    } catch (error) {
      console.error(`Failed to start agent workflow for ${party_id}:`, error);
      closeSnackbar();
      enqueueSnackbar('Failed to start agent', { variant: 'error' });
    }
  };

  const handleAgentAction = async (party_id: string, agentCustom_ui: string | undefined) => {
    skipAllAgentsStatusCheck.current = true;
    try {
      if (agentCustom_ui === 'internal' && !agentStatusList.statuses.find((item) => item._id === party_id)) {
        return;
      }
      const agentStatusResponse = await getSpecificAgentStatus({ party_id }).unwrap();
      console.log('Agent status', agentStatusResponse.status, agentStartedAlready);
      if (agentStatusResponse.status === 'STOPPED' && !agentStartedAlready.get(party_id)) {
        setAgentStartedAlready((prev) => new Map(prev).set(party_id, true));
        await startAgentWorkflow(party_id);
      }

      if (agentCustom_ui === 'internal' && agentStatusResponse.status === 'RUNNING') {
        await updateAgentStatusMutation({ party_id }).unwrap();
        enqueueSnackbar('Agent status updated successfully', { variant: 'success' });
      }
      return agentStatusResponse.status;
    } catch (error) {
      console.error(`Failed to handle agent action for ${party_id}:`, error);
    }
  };
  const setAgentStartedAlreadyForAgent = (party_id: string, value: boolean) => {
    setAgentStartedAlready((prev) => {
      const newMap = new Map(prev);
      if (value) {
        newMap.set(party_id, value);
      } else {
        newMap.delete(party_id);
      }
      return newMap;
    });
  };

  return {
    handleAgentAction,
    getAgentsStatus,
    setAgentStartedAlready: setAgentStartedAlreadyForAgent,
    skipAllAgentsStatusCheck,
  };
};
